import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ButtonPrimary, ButtonTertiary } from '@anwb/poncho/components/button';
import Form from '@anwb/poncho/components/form';
import FormFooter from '@anwb/poncho/components/form-footer';
import Typography from '@anwb/poncho/components/typography';
import { VALIDATION_FAILURE_REDIRECT_EVENTACTION } from 'constants/datalayer';
import { useGaFunnelInfo } from 'hooks/useGaFunnelInfo';
import { useScrollToTop } from 'hooks/useScrollToTop';
import {
	isOccasionSelector,
	leaseCarDataIdSelector,
	paymentInAdvanceChoiceSelector,
	stateSelector,
} from 'store/selectors';
import { STEPS, gotoStep } from 'utils/navigation';
import { scrollToError } from 'utils/scrollToError';
import { overviewValidator } from 'utils/stepValidators/overviewValidator';
import { thankYouValidator } from 'utils/stepValidators/thankYouValidator';
import { trackEvent } from 'utils/trackEvent';

import {
	ColorAndOptions,
	KilometrageAndContractTerm,
	PaymentInAdvance,
	PrivacyStatement,
	UserInformation,
} from './components';
import { mapStoreToLeadRequest, sendLeadRequest } from './util';
import { pushPurchaseToDataLayer } from './util/pushPurchaseToDataLayer';

type OverviewProps = {
	/**
	 * Callback function used in step 3 to update the application's health status and
	 * display the corresponding service health error message from the Requestflow
	 * when a form submission fails.
	 *
	 * @param isSubmitSuccessful - Indicates whether the form submission was successful.
	 */
	onAppHealthChange: (isSubmitSuccesful: boolean) => void;
};

export const Overview = ({ onAppHealthChange }: OverviewProps) => {
	const navigate = useNavigate();
	const state = useSelector(stateSelector);
	const isOccasion = useSelector(isOccasionSelector);
	const paymentInAdvanceChoice = useSelector(paymentInAdvanceChoiceSelector);

	const [loadingFormSubmission, setLoadingFormSubmission] = useState(false);
	const leaseCarId = useSelector(leaseCarDataIdSelector);
	const gaFunnelInfo = useGaFunnelInfo();

	useScrollToTop();

	const onFormSubmit = (): void => {
		const valid = thankYouValidator(state);

		if (!leaseCarId) {
			onAppHealthChange(false);
			window.scrollTo(0, 0);
		}

		if (valid && leaseCarId) {
			setLoadingFormSubmission(true);

			sendLeadRequest(leaseCarId, mapStoreToLeadRequest(state))
				.then((response: Response) => {
					pushPurchaseToDataLayer(state, response);
					gotoStep(
						STEPS.THANK_YOU,
						'Verder naar bedanktpagina',
						navigate,
						gaFunnelInfo,
						'navigation-button'
					);
				})
				.catch(() => {
					onAppHealthChange(false);
					window.scrollTo(0, 0);
				})
				.finally(() => setLoadingFormSubmission(false));
		} else {
			scrollToError();
			!valid &&
				leaseCarId &&
				trackEvent({
					event: 'user_interaction',
					type: 'form',
					component: 'button',
					field: '',
					action: 'click',
					actionValue: 'verder-naar-bedanktpagina',
					status: 'invalid',
				});
		}
	};

	useEffect(() => {
		if (!overviewValidator(state)) {
			gotoStep(STEPS.CAR_CONTRACT, VALIDATION_FAILURE_REDIRECT_EVENTACTION, navigate, gaFunnelInfo);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Form id="private-lease-overview" loading={loadingFormSubmission} onSubmit={onFormSubmit}>
			<Typography className="overview-title" variant="content-title" dataTest="overview-title">
				Controleer je aanvraag
			</Typography>
			<ColorAndOptions />
			<KilometrageAndContractTerm />
			{paymentInAdvanceChoice && <PaymentInAdvance />}
			<UserInformation />
			<PrivacyStatement />
			<FormFooter>
				<ButtonTertiary
					data-test="button-back"
					icon="arrow-left"
					iconPosition="before"
					onClick={() => {
						gotoStep(
							STEPS.USER_INFORMATION,
							'Terug naar step 2',
							navigate,
							gaFunnelInfo,
							'navigation-button'
						);
					}}
				>
					{' '}
					Terug
				</ButtonTertiary>
				<ButtonPrimary data-test="button-submit" className="private_lease_submit" type="submit">
					{`Private Lease${isOccasion ? ' Occasion' : ''} aanvragen`}
				</ButtonPrimary>
			</FormFooter>
		</Form>
	);
};
